/**
 * Onboarding step dots. Screen spec S3, S4 and S5, zone 1.
 *
 * Three marks and a mono counter. The marks are position, not status: they
 * carry no status hue, because "where am I in a three step flow" is not news
 * about whether anything is fine, and spending a status color on furniture is
 * how a product teaches people to ignore its status colors.
 *
 * The counter is the content and the dots are the picture. Read the counter
 * with every color and every shape turned off and the answer is still there,
 * which is the same argument the status pill makes for its word.
 *
 * Each step keeps its name in the accessible tree even though the screen does
 * not print it. A person on a screen reader gets "Connect your code" rather
 * than "step two", and the flow costs no vertical space to say so.
 */

import { t } from '../../lib/i18n/t';
import '../../styles/components/step-dots.css';

export interface StepDotsProps {
  /** One label per step, already through t(). Three of them, today. */
  steps: string[];
  /** Zero based. Everything before it is done, everything after is to come. */
  current: number;
  /** Names the list for a screen reader. */
  label: string;
}

export function StepDots({ steps, current, label }: StepDotsProps) {
  return (
    <div className="dots">
      <ol className="dots__list" aria-label={label}>
        {steps.map((step, index) => (
          <li
            className="dots__item"
            key={step}
            data-state={index < current ? 'done' : index === current ? 'current' : 'todo'}
            aria-current={index === current ? 'step' : undefined}
          >
            <span className="dots__mark" aria-hidden="true" />
            <span className="dots__name">{step}</span>
          </li>
        ))}
      </ol>

      <span className="dots__count mono">
        {t('Step {n} of {total}', { n: current + 1, total: steps.length })}
      </span>
    </div>
  );
}
